"use client";

import { useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { ALL_ALBUM_ID, useAlbums, type Album } from "@/lib/album-context";
import CreateAlbumModal from "@/components/create-album-modal";

/**
 * Left sidebar, in two sections as specified: the user's own albums, then the
 * albums other people have shared with them. Shared entries are labelled with
 * their owner so it is always obvious whose album you are looking at.
 */
export default function AlbumSidebar() {
  const { user } = useAuth();
  const { own, shared, all, current, loading, selectAlbum, refresh } = useAlbums();
  const router = useRouter();
  const pathname = usePathname();
  const [createOpen, setCreateOpen] = useState(false);

  if (!user || user.mustChangePassword) return null;
  if (pathname === "/") return null;

  async function onSelect(id: number) {
    if (id === current && pathname.startsWith("/photos")) return;
    try {
      if (id !== current) await selectAlbum(id);
      router.push("/photos");
      router.refresh();
    } catch {
      router.push("/photos");
    }
  }

  function renderAlbum(album: Album, opts: { showOwner?: boolean } = {}) {
    const active = current === album.id;
    return (
      <li key={album.id}>
        <button
          type="button"
          onClick={() => void onSelect(album.id)}
          title={
            opts.showOwner
              ? `${album.name} — shared by ${album.owner}${
                  album.canContribute ? " (you can add photos)" : " (view only)"
                }`
              : album.name
          }
          className={`w-full text-left px-3 py-2 text-sm transition-colors border-l-2 ${
            active
              ? "border-accent bg-accent/15 text-white"
              : "border-transparent text-muted hover:text-foreground hover:bg-surface-light"
          }`}
        >
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-medium truncate">{album.name}</span>
            <span className="text-[10px] text-muted shrink-0 tabular-nums">
              {album.photoCount}
            </span>
          </div>
          {opts.showOwner && (
            <div className="text-[10px] text-muted truncate">
              from {album.owner}
              {!album.canContribute && " · view only"}
            </div>
          )}
        </button>
      </li>
    );
  }

  return (
    <>
      <aside
        className="fixed top-[50px] bottom-[50px] left-0 z-30 w-56 flex flex-col border-r border-border bg-surface"
        aria-label="Albums"
      >
        <div className="flex items-center justify-between gap-2 px-3 py-3 border-b border-border shrink-0">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted m-0">
            Albums
          </h2>
          <div className="flex items-center gap-2">
            {loading && <span className="text-[10px] text-muted">…</span>}
            <button
              type="button"
              onClick={() => void refresh()}
              className="text-xs text-muted hover:text-accent"
              title="Refresh albums"
            >
              ↻
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-2 min-h-0">
          {all && (
            <ul className="list-none p-0 m-0 mb-1 pb-1 border-b border-border">
              <li>
                <button
                  type="button"
                  onClick={() => void onSelect(ALL_ALBUM_ID)}
                  title="Every photo you can see, across all albums (read-only — pick an album to upload)"
                  className={`w-full text-left px-3 py-2 text-sm transition-colors border-l-2 ${
                    current === ALL_ALBUM_ID
                      ? "border-accent bg-accent/15 text-white"
                      : "border-transparent text-muted hover:text-foreground hover:bg-surface-light"
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium truncate">{all.name}</span>
                    <span className="text-[10px] text-muted shrink-0 tabular-nums">
                      {all.photoCount}
                    </span>
                  </div>
                </button>
              </li>
            </ul>
          )}

          {/* Section 1 — the user's own albums. */}
          <h3 className="px-3 pt-2 pb-1 m-0 text-[10px] font-semibold uppercase tracking-wide text-muted">
            My albums
          </h3>
          {own.length === 0 ? (
            <p className="px-3 text-xs text-muted m-0 leading-relaxed">
              {loading ? "Loading…" : "None yet — use New album below."}
            </p>
          ) : (
            <ul className="list-none p-0 m-0 space-y-0.5">
              {own.map((a) => renderAlbum(a))}
            </ul>
          )}

          {/* Section 2 — albums shared with the user by other people. */}
          <h3 className="px-3 pt-4 pb-1 m-0 text-[10px] font-semibold uppercase tracking-wide text-muted">
            Shared with me
          </h3>
          {shared.length === 0 ? (
            <p className="px-3 text-xs text-muted m-0 leading-relaxed">
              Nothing shared with you yet.
            </p>
          ) : (
            <ul className="list-none p-0 m-0 space-y-0.5">
              {shared.map((a) => renderAlbum(a, { showOwner: true }))}
            </ul>
          )}
        </div>

        <div className="border-t border-border p-2 shrink-0 bg-surface">
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="w-full text-center text-sm font-medium py-2 rounded-md bg-accent text-white hover:bg-accent-hover transition-colors"
          >
            New album
          </button>
        </div>
      </aside>

      <CreateAlbumModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}
